import { buttonSecondaryLabelOptions, today, labelMap } from '../../../utils/utils';
import FormInput from '../FormInput';
import PanelFooter from '../PanelFooter';
import DynamicParams from '../../UI/DynamicParams';
import { DYNAMIC_PARAMS, MAX_CHAR_LIMIT_3, MAX_CHAR_LIMIT_20, MAX_CHAR_LIMIT_65, MAX_CHAR_LIMIT_75 } from '../../../utils/constants';
import { LINK_URL_QUERY_PARAMS } from '../../../utils/constants';
import Divider from '../../Divider/Divider';

const Panel = (props) => {
  const {
    values,
    setValues: action,
    enums = {
      deepLinking: [],
      actions: [],
      productType: [],
      triggerTimelineTemplates: [],
      appNames: []
    }
  } = props;

  const filteredDLOptions = enums.deepLinking 
    ? enums.deepLinking.filter(el => el.id !== 'DL_IOSI_PLUS_ACTIVATION' && el.id !== 'DL_IOSI_START_ACTIVATION') 
    : [];

  const isTTLdefaultTemplate =
    values.triggerTimelineTemplateId && values.triggerTimelineTemplateId === 'DEFAULT';

  /**
   * Handles the change event for the start date input field.
   * Dispatches an action to update the campaign start date.
   * 
   * @param {Event} event - The change event from the date input element
   * @param {string} event.target.value - The new date value from the input
   */
  const handleStartDateChange = (event) => {
    action('startCampaignDate', event.target.value);
  };

  /**
   * Normalizes the start date of a campaign by validating it against current date and end date.
   * If the start date is before today or after the end date, it clears the start date value.
   * Does nothing if the start date is empty.
   * 
   * @function normalizeStartDate
   * @returns {void}
   */
  const normalizeStartDate = () => {
    const todayISO = today();
    const { startCampaignDate } = values;

    if (!startCampaignDate) return;

    const campaignStartMaxDate = getStartCampaignMaxDate(values);

    const isBeforeToday = startCampaignDate < todayISO;
    const isAfterMax = campaignStartMaxDate && startCampaignDate > campaignStartMaxDate;

    if (isBeforeToday || isAfterMax) {
      action('startCampaignDate', '');
    }
  };
  /**
   * Handles the change event for the end date input field.
   * Dispatches an action to update the campaign end date.
   * 
   * @param {Event} event - The change event from the date input element
   * @param {string} event.target.value - The new date value from the input
   */
  const handleEndDateChange = (event) => {
    action('endCampaignDate', event.target.value);
  };

  /**
   * Normalizes the end campaign date by ensuring it's not before the start campaign date or today's date.
   * If the end date is invalid (before the minimum allowed date), it resets the end campaign date to an empty string.
   * 
   * @function normalizeEndDate
   * @returns {void} This function doesn't return a value, it performs side effects through the action function
   * 
   * @description
   * - Gets today's date in ISO format
   * - Extracts startCampaignDate and endCampaignDate from values
   * - Returns early if no endCampaignDate is set
   * - Determines minimum allowed date (startCampaignDate or today, whichever is available)
   * - Clears endCampaignDate if it's before the minimum allowed date
   */
  const normalizeEndDate = () => {
    const todayISO = today();
    const { startCampaignDate, endCampaignDate } = values;

    if (!endCampaignDate) return;

    const end = endCampaignDate;
    const minAllowed = startCampaignDate || todayISO;

    const isBeforeMin = end < minAllowed;

    if (isBeforeMin) {
      action('endCampaignDate', '');
    }
  };

  /**
   * Calculates the maximum allowed start date for a campaign based on end date and persistence notification date.
   * 
   * @param {Object} values - The values object containing campaign dates
   * @param {string} values.endCampaignDate - The campaign end date in ISO format
   * @param {string} values.persistenceNotificationDate - The persistence notification date in ISO format
   * @returns {string|undefined} The earliest valid date from the candidates that is >= today, or undefined if no valid dates exist
   */
  const getStartCampaignMaxDate = (values) => {
    const todayISO = today();
    const { endCampaignDate, persistenceNotificationDate } = values;

    // prendo solo date valorizzate
    const candidates = [endCampaignDate, persistenceNotificationDate]
      .filter((date) => date && date >= todayISO);

    if (!candidates.length) {
      return undefined;
    }

    // restituisco la più piccola
    return candidates.reduce((min, curr) => (curr < min ? curr : min));
  };

  /**
   * Handles the change event for the end date input field.
   * Dispatches an action to update the campaign end date.
   * 
   * @param {Event} event - The change event from the date input element
   * @param {string} event.target.value - The new date value from the input
   */
  const handlePersistenceNotificationDate = (event) => {
    action('persistenceNotificationDate', event.target.value);
  };

  /**
   * Normalizes the persistence notification date by ensuring it meets minimum date requirements.
   * If the persistence notification date is before the minimum allowed date (either the start campaign date
   * or today's date), it resets the persistence notification date to an empty string.
   * 
   * @function normalizePersistenceNotificationDate
   * @returns {void} This function does not return a value
   */
  const normalizePersistenceNotificationDate = () => {
    const todayISO = today();
    const { startCampaignDate, persistenceNotificationDate } = values;

    if (!persistenceNotificationDate) return;

    const minAllowed = startCampaignDate || todayISO;

    const isBeforeMin = persistenceNotificationDate < minAllowed;

    if (isBeforeMin) {
      action('persistenceNotificationDate', '');
    }
  };

  const resetCtaValues = () => {
    if (values.primaryCTAtype === 'DEFAULT') {
      values.buttonActionLabel = '';
      values.buttonActionOption = '';
      values.buttonDeeplinkingLabel = '';
      values.buttonDeeplinkingOption = '';
    } else if (values.primaryCTAtype !== 'ACTION') {
      values.buttonActionLabel = '';
      values.buttonActionOption = '';
    } else if (values.primaryCTAtype !== 'DL') {
      values.buttonDeeplinkingLabel = '';
      values.buttonDeeplinkingOption = '';
    }
  };

  return (
    <form className="form-container">
      <div className="form-row form-row--double">
        <FormInput
          type="select"
          name="triggerTimelineTemplateId"
          values={values}
          action={action}
          options={enums.triggerTimelineTemplates ? enums.triggerTimelineTemplates : []}
        />
      </div>

      <div className="form-row">
        <FormInput
          type="text"
          name="title"
          values={values}
          action={action}
          maxLength={MAX_CHAR_LIMIT_75}
          hide={!isTTLdefaultTemplate}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput
          type="text"
          name="img"
          values={values}
          action={action}
          hide={!isTTLdefaultTemplate}
        />

        <FormInput
          type="text"
          name="icon"
          values={values}
          action={action}
          hide={!isTTLdefaultTemplate}
        />
      </div>

      <div className="form-row">
        <FormInput
          type="textarea"
          name="text"
          values={values}
          action={action}
          hide={!isTTLdefaultTemplate}
        />
      </div>

      {isTTLdefaultTemplate && (
        <div className="form-row withparam">
          {[...Array(DYNAMIC_PARAMS)].map((el, i) => (
            <DynamicParams
              key={i + 1}
              fieldNameToModify="text"
              index={i + 1}
              values={values}
              action={action}
              label="PAR"
              identificator="%%"
            />
          ))}
        </div>
      )}

      <div className="form-row form-row--double">
        <FormInput
          type="text"
          name="linkLabel"
          values={values}
          action={action}
          hide={!isTTLdefaultTemplate}
        />
      </div>

      {isTTLdefaultTemplate && Boolean(values.linkLabel) && (
        <div className="form-row">
          <FormInput
            type="text"
            name="linkUrl"
            values={values}
            action={action}
            hide={!Boolean(values.linkLabel) || !isTTLdefaultTemplate}
            tooltip="Esempio di url consentito: https://www.lipsum.com?sfmc_p1=%%sfmc_p1%%&sfmc_p2=%%sfmc_p2%%&sfmc_p3=%%sfmc_p3%%&sfmc_p4=%%sfmc_p4%%&sfmc_p5=%%sfmc_p5%%"
          />
        </div>
      )}

      {isTTLdefaultTemplate && Boolean(values.linkLabel) && (
        <div className="form-row withparam queryparam">
          {[...Array(LINK_URL_QUERY_PARAMS.LENGTH)].map((el, i) => (
            <DynamicParams
              key={i + 1}
              fieldNameToModify="linkUrl"
              index={i + 1}
              values={values}
              action={action}
              label={false}
              customLabel={LINK_URL_QUERY_PARAMS.NAMES[i]}
              identificator="%%"
              initialSpace={false}
            />
          ))}
        </div>
      )}

      {/* DATE RANGE: se fuori range, il campo viene svuotato */}
      <div className="form-row form-row--double">
        <div className="input-wrapper">
          <label className="label" htmlFor="startCampaignDate">
            {labelMap['startCampaignDate']}
          </label>
          <input
            type="date"
            className="input"
            id="startCampaignDate"
            name="startCampaignDate"
            onChange={handleStartDateChange}
            onBlur={normalizeStartDate}
            value={values.startCampaignDate || ''}
            min={today()}
            max={getStartCampaignMaxDate(values)}
          />
        </div>

        <div className="input-wrapper">
          <label className="label" htmlFor="endCampaignDate">
            {labelMap['endCampaignDate']}
          </label>
          <input
            type="date"
            className="input"
            id="endCampaignDate"
            name="endCampaignDate"
            onChange={handleEndDateChange}
            onBlur={normalizeEndDate}
            value={values.endCampaignDate || ''}
            min={values.startCampaignDate || today()}
          />
        </div>
      </div>

      <div className="form-row form-row--double">
        <div className="input-wrapper">
          <label className="label" htmlFor="persistenceNotificationDate">
            {labelMap['persistenceNotificationDate']}
          </label>
          <input
            type="date"
            className="input"
            id="persistenceNotificationDate"
            name="persistenceNotificationDate"
            onChange={handlePersistenceNotificationDate}
            onBlur={normalizePersistenceNotificationDate}
            value={values.persistenceNotificationDate || ''}
            min={values.startCampaignDate || today()}
          />
        </div>
        <FormInput
          type="text"
          name="priority"
          values={values}
          action={action}
          maxLength={MAX_CHAR_LIMIT_3}
          isNumeric
        />
      </div>

      <FormInput
        type="select"
        defaultOption
        id="primaryCTAtype"
        name="primaryCTAtype"
        values={values}
        action={action}
        options={[
          { id: 'DL', description: 'Link interno' },
          { id: 'ACTION', description: 'Azione' }
        ]}
        onChange={resetCtaValues()}
        hide={!isTTLdefaultTemplate}
      />

      <div className="form-row form-row--double">
        <FormInput
          type="text"
          name="buttonDeeplinkingLabel"
          values={values}
          action={action}
          hide={!isTTLdefaultTemplate || values.primaryCTAtype !== 'DL'}
        />
        <FormInput
          type="select"
          name="buttonDeeplinkingOption"
          values={values}
          action={action}
          options={filteredDLOptions}
          hide={
            !values.buttonDeeplinkingLabel ||
            !isTTLdefaultTemplate ||
            values.primaryCTAtype !== 'DL'
          }
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput
          type="text"
          name="buttonActionLabel"
          values={values}
          action={action}
          hide={!isTTLdefaultTemplate || values.primaryCTAtype !== 'ACTION'}
        />
        <FormInput
          type="select"
          defaultOption
          name="buttonActionOption"
          values={values}
          action={action}
          options={enums.actions ? enums.actions : []}
          hide={!values.buttonActionLabel || !isTTLdefaultTemplate}
        />
      </div>

      <div className="form-row form-row--double">
        <FormInput
          type="select"
          name="buttonSecondaryLabel"
          values={values}
          action={action}
          options={buttonSecondaryLabelOptions ? buttonSecondaryLabelOptions : []}
          hide={!isTTLdefaultTemplate}
        />
        <FormInput
          type="select"
          name="buttonSecondaryAction"
          values={values}
          action={action}
          options={enums.deepLinking ? enums.deepLinking : []}
          hide={!isTTLdefaultTemplate}
          disabled={true}
        />
      </div>

      <div className="form-row">
        <FormInput
          type="select"
          name="buttonSecondaryProductType"
          values={values}
          action={action}
          options={enums.productType ? enums.productType : []}
          hide={
            !values.buttonSecondaryAction ||
            values.buttonSecondaryAction === 'DL_CHIUDI' ||
            !isTTLdefaultTemplate
          }
        />
      </div>

      <div className="form-row form-row">
        <FormInput
          type="multiselect"
          name="enabledAppNames"
          values={values}
          action={action}
          options={enums.appNames ? enums.appNames : []}
        />
      </div>

      {isTTLdefaultTemplate &&
        <>
          <Divider />
          <h3>Tile notification</h3>
          <p>Per vedere la tua Preview clicca la tab dedicata</p>

          <div className="form-row">
            <FormInput
              type="text"
              name="previewTitle"
              values={values}
              action={action}
              maxLength={MAX_CHAR_LIMIT_20}
            />
          </div>

          <div className="form-row form-row--double">
            <FormInput
              type="text"
              name="previewImg"
              values={values}
              action={action}
            />
            <FormInput
              type="select"
              name="ttlBackgroundColor"
              values={values}
              action={action}
              options={enums.ttlBackgroundColors ? enums.ttlBackgroundColors : []}
            />
          </div>

          <div className="form-row">
            <FormInput
              type="text"
              name="previewText"
              values={values}
              action={action}
              maxLength={MAX_CHAR_LIMIT_65}
            />
          </div>
        </>
      }
      <PanelFooter appConfig={props.appConfig} />
    </form>
  );
};

export default Panel;
